/**
 * A/B Test Parameter Registry
 *
 * To add a new testable parameter:
 * 1. Add an entry to CONDITION_PARAMS below
 * 2. Add the corresponding resolver in Go's condition_engine.go
 *
 * Nothing else needs to change.
 */

export type ParamValueType = 'multiselect' | 'text';

export interface ParamOption {
  value: string;
  label: string;
}

export interface ParamConfig {
  label: string;
  valueType: ParamValueType;
  options?: ParamOption[];
  operators: string[];
  placeholder?: string;
}

export const CONDITION_PARAMS: Record<string, ParamConfig> = {
  country: {
    label: 'Country',
    valueType: 'multiselect',
    operators: ['in', 'not_in'],
    options: [
      { value: 'US', label: '🇺🇸 United States' },
      { value: 'GB', label: '🇬🇧 United Kingdom' },
      { value: 'CA', label: '🇨🇦 Canada' },
      { value: 'AU', label: '🇦🇺 Australia' },
      { value: 'DE', label: '🇩🇪 Germany' },
      { value: 'FR', label: '🇫🇷 France' },
      { value: 'IT', label: '🇮🇹 Italy' },
      { value: 'ES', label: '🇪🇸 Spain' },
      { value: 'NL', label: '🇳🇱 Netherlands' },
      { value: 'PL', label: '🇵🇱 Poland' },
      { value: 'BR', label: '🇧🇷 Brazil' },
      { value: 'MX', label: '🇲🇽 Mexico' },
      { value: 'IN', label: '🇮🇳 India' },
      { value: 'JP', label: '🇯🇵 Japan' },
      { value: 'KR', label: '🇰🇷 South Korea' },
      { value: 'RU', label: '🇷🇺 Russia' },
      { value: 'UA', label: '🇺🇦 Ukraine' },
      { value: 'TR', label: '🇹🇷 Turkey' },
      { value: 'SA', label: '🇸🇦 Saudi Arabia' },
      { value: 'AE', label: '🇦🇪 UAE' },
      { value: 'ZA', label: '🇿🇦 South Africa' },
      { value: 'NG', label: '🇳🇬 Nigeria' },
      { value: 'SG', label: '🇸🇬 Singapore' },
      { value: 'TH', label: '🇹🇭 Thailand' },
      { value: 'ID', label: '🇮🇩 Indonesia' },
      { value: 'PH', label: '🇵🇭 Philippines' },
      { value: 'VN', label: '🇻🇳 Vietnam' },
      { value: 'MY', label: '🇲🇾 Malaysia' },
      { value: 'PK', label: '🇵🇰 Pakistan' },
      { value: 'BD', label: '🇧🇩 Bangladesh' },
      { value: 'EG', label: '🇪🇬 Egypt' },
      { value: 'AR', label: '🇦🇷 Argentina' },
      { value: 'CO', label: '🇨🇴 Colombia' },
      { value: 'CL', label: '🇨🇱 Chile' },
      { value: 'PT', label: '🇵🇹 Portugal' },
      { value: 'SE', label: '🇸🇪 Sweden' },
      { value: 'NO', label: '🇳🇴 Norway' },
      { value: 'DK', label: '🇩🇰 Denmark' },
      { value: 'FI', label: '🇫🇮 Finland' },
      { value: 'CH', label: '🇨🇭 Switzerland' },
      { value: 'AT', label: '🇦🇹 Austria' },
      { value: 'BE', label: '🇧🇪 Belgium' },
      { value: 'CZ', label: '🇨🇿 Czech Republic' },
      { value: 'RO', label: '🇷🇴 Romania' },
      { value: 'HU', label: '🇭🇺 Hungary' },
      { value: 'GR', label: '🇬🇷 Greece' },
      { value: 'CN', label: '🇨🇳 China' },
      { value: 'HK', label: '🇭🇰 Hong Kong' },
      { value: 'TW', label: '🇹🇼 Taiwan' },
      { value: 'IL', label: '🇮🇱 Israel' },
      { value: 'NZ', label: '🇳🇿 New Zealand' },
    ],
  },
  language: {
    label: 'Language',
    valueType: 'multiselect',
    operators: ['in', 'not_in', 'starts_with'],
    options: [
      { value: 'en', label: 'English' },
      { value: 'es', label: 'Spanish' },
      { value: 'fr', label: 'French' },
      { value: 'de', label: 'German' },
      { value: 'it', label: 'Italian' },
      { value: 'pt', label: 'Portuguese' },
      { value: 'ru', label: 'Russian' },
      { value: 'zh', label: 'Chinese' },
      { value: 'ja', label: 'Japanese' },
      { value: 'ko', label: 'Korean' },
      { value: 'ar', label: 'Arabic' },
      { value: 'hi', label: 'Hindi' },
      { value: 'tr', label: 'Turkish' },
      { value: 'nl', label: 'Dutch' },
      { value: 'pl', label: 'Polish' },
      { value: 'uk', label: 'Ukrainian' },
      { value: 'sv', label: 'Swedish' },
      { value: 'no', label: 'Norwegian' },
      { value: 'da', label: 'Danish' },
      { value: 'fi', label: 'Finnish' },
      { value: 'cs', label: 'Czech' },
      { value: 'ro', label: 'Romanian' },
      { value: 'hu', label: 'Hungarian' },
      { value: 'el', label: 'Greek' },
      { value: 'vi', label: 'Vietnamese' },
      { value: 'th', label: 'Thai' },
      { value: 'id', label: 'Indonesian' },
      { value: 'ms', label: 'Malay' },
    ],
  },
  device: {
    label: 'Device',
    valueType: 'multiselect',
    operators: ['in', 'not_in'],
    options: [
      { value: 'mobile', label: 'Mobile' },
      { value: 'desktop', label: 'Desktop' },
      { value: 'tablet', label: 'Tablet' },
    ],
  },
  os: {
    label: 'Operating System',
    valueType: 'multiselect',
    operators: ['in', 'not_in'],
    options: [
      { value: 'windows', label: 'Windows' },
      { value: 'macos', label: 'macOS' },
      { value: 'ios', label: 'iOS' },
      { value: 'android', label: 'Android' },
      { value: 'linux', label: 'Linux' },
    ],
  },
  browser: {
    label: 'Browser',
    valueType: 'multiselect',
    operators: ['in', 'not_in'],
    options: [
      { value: 'chrome', label: 'Chrome' },
      { value: 'firefox', label: 'Firefox' },
      { value: 'safari', label: 'Safari' },
      { value: 'edge', label: 'Edge' },
      { value: 'opera', label: 'Opera' },
      { value: 'samsung browser', label: 'Samsung Browser' },
    ],
  },
};

export const OPERATOR_LABELS: Record<string, string> = {
  in: 'is',
  not_in: 'is not',
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  starts_with: 'starts with',
  regex: 'matches regex',
};
